import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Alert, Button, IconButton, Tooltip, TextField, Typography, COLORS,
  Modal, ModalHeader, ModalContent, ModalFooter, ModalButtons,
} from 'pvs-design-system';
import { Box, Stack } from '@mui/material';
import Help from '@mui/icons-material/Help';
import TreeSelectField from 'components/virtual-library/TreeSelectField';
import ExcludeLocationsField from 'components/virtual-library/ExcludeLocationsField';
import ModalRouteBackground from 'components/virtual-library/ModalRouteBackground';
import { Notify } from 'components/Notifications/Notifications';
import { extractErrorMessage } from 'utils/errorUtils';
import { useAdminFormTrees, useModalClose } from 'hooks';
import {
  useCategoryCreateInitQuery,
  useCreateAdminCategoryMutation,
} from 'api/useLibraryApi';
import type { FlatTreeNode } from 'types';
import type { ExcludedLocation } from 'types/LibraryApi';
import { SHIMMER_PURPLE_SX } from 'constants/styles';

/**
 * Computes the lineage (parent path) for a new category given the selected
 * parentCategoryId and the flat parentCategories tree.
 * Walks up via idParent until reaching the 'Home' root sentinel.
 */
function computeLineageForParent(parentId: number | null, nodes: FlatTreeNode[]): string {
  if (parentId === null) return '/Home/';
  const nodeMap = new Map(nodes.map((n) => [n.id, n]));
  const parts: string[] = [];
  let current = nodeMap.get(parentId);
  while (current) {
    if (current.name === 'Home' || current.idParent === null) break;
    parts.unshift(current.name);
    current = current.idParent !== null ? nodeMap.get(current.idParent) : undefined;
  }
  return '/Home/' + (parts.length > 0 ? parts.join('/') + '/' : '');
}

const MAX_SEGMENT_LENGTH = 50;
const NAME_PATTERN_HINT =
  'Use this field to specify the name of the folder you would like to create. ' +
  'Create multiple folders at once. Ex: /Main Folder/Subfolder (50 character limit per name)';
const LOCATION_HINT =
  'This can be used to indicate the location at which you would like to create a new folder.';
const PARENT_FOLDER_HINT =
  'Use this selection tree to specify which folder you would like to create your new folder under.';
const EXCLUDE_LOCATIONS_HINT =
  'Use this to identify any locations that should not have access to the folder regardless of if they otherwise have permission to it.';

const TIME_SAVING_TIP =
  'Create multiple folders at once by separating each with slashes. Example: Entering /MyFolder/MySubfolder into the Name field will result in a folder named MyFolder being created under the currently selected parent folder, followed by MySubfolder being created under MyFolder.';

const validateName = (value: string): string => {
  if (!value.trim()) return 'Name is required.';
  const segments = value.split('/').filter(Boolean);
  for (const seg of segments) {
    if (seg.trim().length > MAX_SEGMENT_LENGTH) {
      return `Each folder name must be ${MAX_SEGMENT_LENGTH} characters or fewer.`;
    }
  }
  return '';
};

const CreateCategoryPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const navState = location.state as { parentCategoryId?: string; lineage?: string } | null;
  const navLineage = navState?.lineage ?? '/Home/';

  // ── Form state ─────────────────────────────────────────────────────
  const [name, setName] = useState('');
  const [nameError, setNameError] = useState('');
  const [locationId, setLocationId] = useState<number | null>(null);
  const [locationError, setLocationError] = useState('');
  const [parentCategoryId, setParentCategoryId] = useState<number | null>(null);
  const [excludedLocations, setExcludedLocations] = useState<ExcludedLocation[]>([]);

  // Tree state and lazy loaders
  const {
    locationNodes,
    setLocationNodes,
    handleLoadLocationChildren,
    categoryNodes,
    fetchCategoriesForLocation,
    handleLoadCategoryChildren,
  } = useAdminFormTrees();

  // The init query is scoped to the lineage the modal was opened from.
  // navLineage is fixed at mount time (from router state) and does not change.
  const {
    locations,
    defaultLocationId,
    defaultCategoryId,
    isLoading: initLoading,
    isError: initError,
    error: initErrorObj,
  } = useCategoryCreateInitQuery(navLineage);

  // Build a user-friendly message when init fails (shown as inline alert)
  const initErrorMessage = initError
    ? extractErrorMessage(initErrorObj, 'Failed to load folder form. Please try again.')
    : null;

  // Sync init locations into lazily-loaded state
  React.useEffect(() => {
    if (locations.length > 0) setLocationNodes(locations);
  }, [locations]);

  const loadingLocations = initLoading;
  const locationsError = initError;
  const loadingCategories = initLoading;

  // Apply API defaults on first load
  const [defaultsApplied, setDefaultsApplied] = useState(false);
  useEffect(() => {
    if (!defaultsApplied && defaultLocationId !== null) {
      setLocationId(defaultLocationId);
      setParentCategoryId(defaultCategoryId);
      fetchCategoriesForLocation(defaultLocationId);
      setDefaultsApplied(true);
    }
  }, [defaultLocationId, defaultCategoryId, defaultsApplied, fetchCategoriesForLocation]);

  const [createCategory, { isLoading: saving }] = useCreateAdminCategoryMutation();

  // ── Helpers ────────────────────────────────────────────────────────
  const validate = (): boolean => {
    let valid = true;

    const nErr = validateName(name);
    setNameError(nErr);
    if (nErr) valid = false;

    if (locationId === null) {
      setLocationError('Location is required.');
      valid = false;
    } else {
      setLocationError('');
    }

    return valid;
  };

  const handleLocationSelect = useCallback((id: number) => {
    setLocationId(id);
    setLocationError('');
    setParentCategoryId(null);
    // Fetch parent categories scoped to the selected location via API.
    fetchCategoriesForLocation(id);
  }, [fetchCategoriesForLocation]);

  const handleSave = async (andFinish: boolean) => {
    if (!validate()) return;

    const lineage = computeLineageForParent(parentCategoryId, categoryNodes);

    try {
      await createCategory({
        name: name.trim(),
        lineage,
        locationId: locationId!,
        parentCategoryId,
        excludedLocations,
      }).unwrap();

      const segments = name.trim().split('/').filter(Boolean);
      const successMsg = segments.length > 1
        ? `${segments.length} folders created successfully: ${segments.map((s) => `'${s.trim()}'`).join(', ')}.`
        : `Folder '${segments[0]?.trim() ?? name.trim()}' created successfully.`;

      if (andFinish) {
        Notify.success(successMsg);
        navigate(-1);
      } else {
        // Show toast and reset entry fields for the next category
        Notify.success(successMsg);
        setName('');
        setNameError('');
        setExcludedLocations([]);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    } catch (e) {
      Notify.error(extractErrorMessage(e, 'Failed to create folder. Please try again.'));
    }
  };

  const handleClose = useModalClose();

  if (initLoading) {
    const shimmerSx = SHIMMER_PURPLE_SX;
    return (
      <>
      <ModalRouteBackground />
      <Modal open onClose={handleClose} closeModalOnBackdropClick ariaLabelledby="create-folder-title" PaperProps={{ sx: { width: '800px', maxWidth: '800px' } }}>
        <ModalHeader id="create-folder-title" title="Create New Folder" onClose={handleClose} />
        <ModalContent>
        <Stack gap={3}>
          <Box>
            <Box sx={{ ...shimmerSx, width: 50, height: 14, mb: 1 }} />
            <Box sx={{ ...shimmerSx, width: '100%', height: 40 }} />
          </Box>
          <Box>
            <Box sx={{ ...shimmerSx, width: 70, height: 14, mb: 1 }} />
            <Box sx={{ ...shimmerSx, width: '100%', height: 40 }} />
          </Box>
          <Box>
            <Box sx={{ ...shimmerSx, width: 120, height: 14, mb: 1 }} />
            <Box sx={{ ...shimmerSx, width: '100%', height: 40 }} />
          </Box>
          <Stack direction="row" gap={1.5} pt={1}>
            <Box sx={{ ...shimmerSx, width: 90, height: 40, borderRadius: 1 }} />
            <Box sx={{ ...shimmerSx, width: 140, height: 40, borderRadius: 1 }} />
            <Box sx={{ ...shimmerSx, width: 160, height: 40, borderRadius: 1 }} />
          </Stack>
        </Stack>
        </ModalContent>
      </Modal>
      </>
    );
  }

  return (
    <>
    <ModalRouteBackground />
    <Modal open onClose={handleClose} closeModalOnBackdropClick ariaLabelledby="create-folder-title" PaperProps={{ sx: { width: '800px', maxWidth: '800px' } }}>
      <Box sx={{ position: 'relative' }}>
        <ModalHeader id="create-folder-title" title="Create New Folder" onClose={handleClose} />
        <Tooltip title={`Time-Saving Tip: ${TIME_SAVING_TIP}`} placement="bottom">
          <Box component="span" sx={{ position: 'absolute', top: 0, right: 48 }}>
            <IconButton size="medium" color="secondary" aria-label="Time-saving tip">
              <Help />
            </IconButton>
          </Box>
        </Tooltip>
      </Box>

      <ModalContent>
        {/* Inline init error — form stays visible but Save is disabled */}
        {initErrorMessage && (
          <Alert severity="error" sx={{ mb: 2 }}>{initErrorMessage}</Alert>
        )}

        {/* ── Form ────────────────────────────────────────────────────── */}
        <form
          id="create-folder-form"
          noValidate
          onSubmit={(e) => {
            e.preventDefault();
            handleSave(false);
          }}
        >
          <Stack gap={3}>
            {/* Name */}
            <TextField
              id="cat-name"
              label="Name"
              value={name}
              size="medium"
              fullWidth
              inputProps={{ maxLength: 255 }}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setName(e.target.value);
                if (nameError) setNameError(validateName(e.target.value));
              }}
              error={!!nameError}
              errorMessage={nameError}
              helperText={!nameError ? NAME_PATTERN_HINT : undefined}
            />

            {/* Location */}
            <TreeSelectField
              id="cat-location"
              label="Location"
              nodes={locationNodes}
              selectedId={locationId}
              onSelect={handleLocationSelect}
              onLoadChildren={handleLoadLocationChildren}
              placeholder="Select a location…"
              loading={loadingLocations}
              error={!!locationError || locationsError}
              errorMessage={locationError || (locationsError ? 'Failed to load locations.' : undefined)}
              helperText={LOCATION_HINT}
            />

            {/* Parent Category */}
            <TreeSelectField
              id="cat-parent"
              label="Parent Folder"
              nodes={categoryNodes}
              selectedId={parentCategoryId}
              onSelect={setParentCategoryId}
              onLoadChildren={handleLoadCategoryChildren}
              placeholder="Choose a Library Resource Folder"
              disabled={locationId === null}
              disabledPlaceholder="Select a location first"
              loading={loadingCategories}
              helperText={PARENT_FOLDER_HINT}
            />

            {/* Exclude from locations */}
            <ExcludeLocationsField
              value={excludedLocations}
              locationId={locationId}
              onChange={setExcludedLocations}
              isCreateMode
              helperText={EXCLUDE_LOCATIONS_HINT}
            />
          </Stack>
        </form>
      </ModalContent>

      <ModalFooter>
        <ModalButtons>
          <Button type="button" size="small" variant="text" color="secondary" onClick={handleClose}>Cancel</Button>
          <Button type="submit" size="small" form="create-folder-form" variant="outlined" color="secondary" disabled={saving || !!initErrorMessage}>Save</Button>
          <Button type="button" size="small" variant="contained" color="primary" disabled={saving || !!initErrorMessage} onClick={() => handleSave(true)}>Save and Finish</Button>
        </ModalButtons>
      </ModalFooter>
    </Modal>
    </>
  );
};

export default CreateCategoryPage;
